import * as React from 'react';
import { observer } from 'mobx-react';

import DropdownContentList from 'shared/newComponents/DropdownContentList';
import { useChannelsModalStore } from 'stores/ChannelsModalStore';
import { useRootStore } from 'stores/index';
import {
  ChannelKind,
  mapWzRevokedReasonToMessage
} from 'shared/entities/channels';
import { DropdownContentItem } from 'shared/newEntities/components/DropdownContent';
import { documentationLinks } from 'shared/entities/app';
import { isCurrentDomain } from 'shared/utils/domainUtils';
import WzOAuthAction from 'shared/newContainers/ChannelsContent/WzOAuthAction';
import Typography from 'shared/newComponents/Typography';
import {
  TypographySize,
  TypographyType
} from 'shared/newEntities/components/Typography';

import Scenarios from './Scenarios';
import Tokens from './Tokens';
import Commands from './Commands';
import TelegramMenu from './TelegramMenu';
import WidgetTesting from './WidgetTesting';
import WidgetSnippet from './WidgetSnippet';

type Props = {
  className?: string;
  footer?: React.ReactNode;
};

enum ContentSectionId {
  scenarios = 'scenarios',
  tokens = 'tokens',
  telegramMenu = 'telegramMenu',
  telegramCommands = 'telegramCommands',
  apiKey = 'apiKey',
  snippet = 'snippet',
  widgetTesting = 'widgetTesting',
  wazzupAccess = 'wazzupAccess'
}

const Content: React.FC<Props> = ({ className, footer }) => {
  const { store } = useChannelsModalStore();
  const rootStore = useRootStore();
  const channel = store.selectedChannel;
  const isWzOAuth = channel?.kind === ChannelKind.WZ && channel.isWzOAuth;

  const items: DropdownContentItem[] = [];

  if (store.showSelectedChannelScenarios) {
    items.push({
      id: ContentSectionId.scenarios,
      title: (t) =>
        t('ChannelsModal.SelectedChannel.Content.Scenarios.title', {
          ns: 'newContainers'
        }),
      children: <Scenarios footer={footer} />
    });
  }

  if (channel?.kind !== ChannelKind.WIDGET && !isWzOAuth) {
    items.push({
      id: ContentSectionId.tokens,
      title: (t) =>
        t(
          `ChannelsModal.SelectedChannel.Content.Tokens.${
            store.selectedChannel?.kind === ChannelKind.VK
              ? 'titleMany'
              : 'titleOne'
          }`,
          {
            ns: 'newContainers'
          }
        ),
      children: <Tokens />
    });
  }

  if (channel?.kind === ChannelKind.WZ) {
    // wzRoutingState != null — канал занят операцией (перевод, переподключение, удаление)
    const isBusy = channel.wzRoutingState !== null;
    const canMigrate =
      rootStore.projectStore.withWzOAuthFeature && channel.canMigrateToOAuth;
    if (isWzOAuth || canMigrate) {
      items.push({
        id: ContentSectionId.wazzupAccess,
        title: (t) => t('wzOAuth.accessTitle', { ns: 'newContainers' }),
        children: (
          <div>
            {isWzOAuth && channel.hasTokenErrors && (
              <Typography
                fontType={TypographyType.text}
                fontSize={TypographySize.s}
              >
                {mapWzRevokedReasonToMessage(channel.wzRevokedReason)(
                  rootStore.translationsStore.t
                )}
              </Typography>
            )}
            {!isBusy && (
              <WzOAuthAction
                channelId={channel.id}
                action={isWzOAuth ? 'reconnect' : 'migrate'}
                isRevoked={channel.hasTokenErrors}
              />
            )}
          </div>
        )
      });
    }
  }

  if (channel?.kind === ChannelKind.TELEGRAM) {
    items.push(
      {
        id: ContentSectionId.telegramMenu,
        title: (t) =>
          t('ChannelsModal.SelectedChannel.Content.MenuTelegram.title', {
            ns: 'newContainers'
          }),
        children: <TelegramMenu channel={channel} />
      },
      {
        id: ContentSectionId.telegramCommands,
        title: (t) =>
          t('ChannelsModal.SelectedChannel.Content.Commands.title', {
            ns: 'newContainers'
          }),
        children: <Commands channel={channel} />,
        reference: {
          title: (t) =>
            t(
              'ChannelsModal.SelectedChannel.Content.Commands.Reference.title',
              {
                ns: 'newContainers'
              }
            ),
          description: (t) =>
            t(
              'ChannelsModal.SelectedChannel.Content.Commands.Reference.description',
              {
                ns: 'newContainers'
              }
            ),
          docsLink: {
            title: (t) =>
              t(
                'ChannelsModal.SelectedChannel.Content.Commands.Reference.docsLink',
                {
                  ns: 'newContainers'
                }
              ),
            link: documentationLinks.menuForTgBot
          }
        }
      }
    );
  }

  if (channel?.kind === ChannelKind.WIDGET) {
    items.push({
      id: ContentSectionId.snippet,
      title: (t) =>
        t('ChannelsModal.SelectedChannel.Content.Widget.Snippet.title', {
          ns: 'newContainers'
        }),
      children: <WidgetSnippet channel={channel} />
    });

    if (isCurrentDomain(channel.domain)) {
      items.push({
        id: ContentSectionId.widgetTesting,
        title: (t) =>
          t('ChannelsModal.SelectedChannel.Content.Widget.Testing.title', {
            ns: 'newContainers'
          }),
        children: <WidgetTesting channel={channel} />
      });
    }
  }

  let defaultSectionId: ContentSectionId | null = null;

  if (channel) {
    switch (true) {
      case store.showSelectedChannelScenarios && !store.isModal:
        defaultSectionId = ContentSectionId.scenarios;
        break;

      case isWzOAuth && channel.hasTokenErrors:
        defaultSectionId = ContentSectionId.wazzupAccess;
        break;

      case channel.hasNoTokens || channel.hasTokenErrors:
        defaultSectionId = ContentSectionId.tokens;
        break;

      default:
        break;
    }
  }

  return (
    <DropdownContentList
      className={className}
      defaultId={defaultSectionId}
      items={items}
    />
  );
};
export default observer(Content);
